/**
 * OperationsHealthCard — StreamX Sprint 3b.
 *
 * Calm, enterprise-grade operations observability tile for the
 * super_admin dashboard.  Aggregates the health of the three
 * operator-facing dispatch workers:
 *
 *   1. Refund worker        (arenax_refund_queue)
 *   2. Webhook dispatcher   (webhook_deliveries)
 *   3. LinkedIn dispatcher  (paused — placeholder)
 *
 * Doctrine
 * ────────
 *   • Premium SaaS infrastructure aesthetic, not terminal logs
 *   • Glowing pulse indicators per status (🟢 healthy / 🟡 delayed / 🔴 attention / ⏸ paused)
 *   • One row per worker, one card overall, single API call
 *   • Auto-refreshes every 30 seconds (matches refund worker drain interval)
 *   • Drill-in link per worker
 */
import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Link } from 'react-router-dom';
import {
  Banknote, Webhook, Linkedin, Activity, ChevronRight, AlertTriangle, Pause,
} from 'lucide-react';

const STATUS_META = {
  healthy:   { label: 'Healthy',            tone: '#10b981', glyph: '🟢', pulseColor: 'rgba(16, 185, 129, 0.6)' },
  delayed:   { label: 'Delayed',            tone: '#FFA500', glyph: '🟡', pulseColor: 'rgba(255, 165, 0, 0.55)' },
  attention: { label: 'Attention required', tone: 'hsl(0 70% 55%)', glyph: '🔴', pulseColor: 'rgba(244, 63, 94, 0.65)' },
  paused:    { label: 'Paused',             tone: '#94a3b8', glyph: '⏸', pulseColor: 'rgba(148, 163, 184, 0.4)' },
};

const KIND_ICON = {
  refund:   Banknote,
  webhook:  Webhook,
  linkedin: Linkedin,
};

function fmtRelative(iso) {
  if (!iso) return '—';
  try {
    const then = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - then);
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  } catch (_) { return iso; }
}

function PulseDot({ tone, pulseColor }) {
  return (
    <span className="relative inline-flex h-2.5 w-2.5">
      <span
        className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping"
        style={{ background: pulseColor }}
      />
      <span
        className="relative inline-flex rounded-full h-2.5 w-2.5"
        style={{ background: tone, boxShadow: `0 0 10px ${pulseColor}` }}
      />
    </span>
  );
}

function WorkerRow({ worker }) {
  const meta = STATUS_META[worker.status] || STATUS_META.healthy;
  const Icon = KIND_ICON[worker.kind] || Activity;
  return (
    <div
      className="flex items-center justify-between gap-3 p-3 rounded-lg border transition-colors hover:bg-white/[0.02]"
      style={{
        background: 'rgba(255, 255, 255, 0.02)',
        borderColor: worker.status === 'attention'
          ? 'hsl(0 70% 55%)'
          : worker.status === 'delayed' ? '#3a2a1a' : '#22314F',
      }}
      data-testid={`ops-worker-${worker.kind}`}
    >
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <div
          className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: `${meta.tone}14`, border: `1px solid ${meta.tone}30` }}
        >
          <Icon className="h-4 w-4" style={{ color: meta.tone }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-sm font-bold text-white truncate">{worker.name}</span>
            <PulseDot tone={meta.tone} pulseColor={meta.pulseColor} />
          </div>
          <div className="text-[11px] truncate" style={{ color: '#8FA3C9' }}>
            {worker.notes}
          </div>
          <div className="text-[10px] font-mono mt-1" style={{ color: '#5a708a' }}>
            <span className="uppercase tracking-wider font-bold" style={{ color: meta.tone }}>
              {meta.label}
            </span>
            <span className="mx-1.5" style={{ color: '#3a4a64' }}>·</span>
            <span>
              {worker.pending} pending
            </span>
            {worker.dead_letter > 0 && (
              <>
                <span className="mx-1.5" style={{ color: '#3a4a64' }}>·</span>
                <span style={{ color: 'hsl(0 70% 55%)' }} className="font-bold">
                  {worker.dead_letter} dead-letter
                </span>
              </>
            )}
            <span className="mx-1.5" style={{ color: '#3a4a64' }}>·</span>
            <span>last success {fmtRelative(worker.last_success_at)}</span>
          </div>
        </div>
      </div>
      {worker.drill_in && (
        <Link
          to={worker.drill_in}
          className="shrink-0 inline-flex items-center text-[10px] uppercase tracking-wider font-bold text-zinc-400 hover:text-white transition-colors"
          data-testid={`ops-worker-drill-${worker.kind}`}
        >
          Inspect <ChevronRight className="h-3 w-3 ml-0.5" />
        </Link>
      )}
    </div>
  );
}

export default function OperationsHealthCard() {
  const [data, setData] = useState({ overall_status: 'healthy', workers: [] });
  const [loading, setLoading] = useState(true);
  const [lastFetchAt, setLastFetchAt] = useState(null);
  const [err, setErr] = useState('');

  const refresh = useCallback(async () => {
    try {
      const r = await api.get('/arenax/admin/operations-health');
      setData(r.data || { overall_status: 'healthy', workers: [] });
      setLastFetchAt(new Date());
      setErr('');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not load ops health');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 30_000);
    return () => clearInterval(id);
  }, [refresh]);

  const overall = STATUS_META[data.overall_status] || STATUS_META.healthy;

  return (
    <div
      className="p-5 rounded-xl border-2"
      style={{
        background: 'linear-gradient(135deg, #07111F 0%, #0a1628 100%)',
        borderColor: data.overall_status === 'attention'
          ? 'hsl(0 70% 55%)'
          : data.overall_status === 'delayed' ? '#3a2a1a' : '#22314F',
      }}
      data-testid="operations-health-card"
    >
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Activity className="h-4 w-4" style={{ color: '#FF5A1F' }} />
            <h3 className="text-base font-black text-white">Operations health</h3>
            <PulseDot tone={overall.tone} pulseColor={overall.pulseColor} />
          </div>
          <div className="text-[11px]" style={{ color: '#8FA3C9' }}>
            Aggregated dispatcher health · refresh every 30s
          </div>
        </div>
        <div
          className="text-[10px] uppercase tracking-[0.18em] font-bold px-3 py-1 rounded-full"
          style={{
            background: `${overall.tone}14`,
            border: `1px solid ${overall.tone}40`,
            color: overall.tone,
          }}
          data-testid="ops-overall-status"
        >
          {overall.label}
        </div>
      </div>

      {loading && data.workers.length === 0 ? (
        <div className="text-[11px] text-center py-6" style={{ color: '#8FA3C9' }}>
          Loading worker status…
        </div>
      ) : err ? (
        <div
          className="p-3 rounded-lg flex items-start gap-2 text-xs"
          style={{ background: 'rgba(244, 63, 94, 0.08)', color: '#fb7185' }}
          data-testid="ops-health-error"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {err}
        </div>
      ) : (
        <div className="space-y-2">
          {data.workers.map((w) => <WorkerRow key={w.kind} worker={w} />)}
        </div>
      )}

      <div className="text-[9px] mt-3 text-right font-mono" style={{ color: '#5a708a' }}>
        {lastFetchAt ? `Last checked ${lastFetchAt.toLocaleTimeString()}` : ''}
      </div>
    </div>
  );
}
